/* Phase 25 (E.1) -- route-level code splitting.
 *
 * Everything below AppShell is loaded lazily. First navigation to a heavy
 * hub (Admin / Sales / HR / Service / Front-Desk / Chat / Notes / PM /
 * Meetings / Portal) fetches its own chunk on demand instead of shipping
 * the entire 4 MB monolith on first paint.
 *
 * Kept EAGER: Login, ForgotPassword, InviteAccept (need instant paint on
 * unauth entry), AppShell + RequireAuth + guards (framework), Dashboard
 * (Home is the FMP target -- shouldn't flicker Suspense on cold load),
 * PageHeader/Shimmer/small hooks (already tiny).
 */
import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster as Sonner } from "@/components/ui/sonner";
import BugReportModal from "@/components/BugReportModal";
import { ConfirmDialogRoot } from "@/components/ui/ConfirmDialog";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useAuth } from "@/store/auth";
import RequireAuth from "@/components/RequireAuth";
import AppShell from "@/components/shell/AppShell";
import { ErrorBoundary } from "@/components/PolishComponents";
import { ShimmerDashboard } from "@/components/polish/Shimmer";
import { usePermission, useMatrixAny } from "@/hooks/usePermission";
import PushPermissionProvider from "@/components/notifications/PushPermissionProvider";
import ChatCompliancePolicy from "@/components/chat/ChatCompliancePolicy";
import DelightProvider from "@/components/delight/DelightProvider";
import { HubRedirect } from "@/components/hub/HubShell";

// ---------------------------------------------------------------------------
// Eager (light, always-shown)
// ---------------------------------------------------------------------------
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import InviteAccept from "@/pages/InviteAccept";
import Dashboard from "@/pages/Dashboard";
import ComingSoon from "@/pages/ComingSoon";
import ResetPasswordConsume from "@/pages/ResetPasswordConsume";
import ForceChangePassword from "@/pages/ForceChangePassword";

// ---------------------------------------------------------------------------
// Lazy helpers -- named exports need a small wrapper because React.lazy only
// understands default exports.
// ---------------------------------------------------------------------------
const named = (loader, name) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

// ---------------------------------------------------------------------------
// Lazy (hubs + heavy modules)
// ---------------------------------------------------------------------------
// -- Admin hub -----------------------------------------------------------
const AdminLayout       = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard    = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminHub          = lazy(() => import("@/pages/admin/AdminHub"));
const AdminUsers        = lazy(() => import("@/pages/AdminUsers"));
const AuditLog          = lazy(() => import("@/pages/AuditLog"));
const ProductionReset   = lazy(() => import("@/pages/admin/ProductionReset"));
const LlmUsage          = lazy(() => import("@/pages/admin/LlmUsage"));
const AutomationsAdmin  = lazy(() => import("@/pages/admin/AutomationsAdmin"));
const DemoData          = lazy(() => import("@/pages/admin/DemoData"));
const ChatCompliance    = lazy(() => import("@/pages/admin/ChatCompliance"));
const NotificationsTemplates = lazy(() => import("@/pages/admin/NotificationsTemplates"));
const AiRulesAdmin      = lazy(() => import("@/pages/admin/rules/AiRulesAdmin"));
const AiActionsLog      = lazy(() => import("@/pages/admin/rules/AiActionsLog"));
const AiCostAdmin       = lazy(() => import("@/pages/admin/rules/AiCostAdmin"));
const DataCleanupAdmin  = lazy(() => import("@/pages/admin/rules/DataCleanupAdmin"));
const HrRulesAdmin      = lazy(() => import("@/pages/admin/rules/HrRulesAdmin"));
const SmtpAdmin         = lazy(() => import("@/pages/admin/rules/SmtpAdmin"));
const PmRulesToggles    = lazy(() => import("@/pages/admin/rules/PmRulesToggles"));
const ModuleVisibilityRules = lazy(() => import("@/pages/admin/rules/ModuleVisibilityRules"));
const GeofenceAdmin     = lazy(() => import("@/pages/admin/rules/GeofenceAdmin"));
const OrgTaxonomyAdmin  = lazy(() => import("@/pages/admin/rules/OrgTaxonomyAdmin"));
const SecurityPolicyAdmin = lazy(() => import("@/pages/admin/rules/SecurityPolicyAdmin"));
const CompanyLegalAdmin = lazy(() => import("@/pages/admin/rules/CompanyLegalAdmin"));  // D37.b
const ChecklistTemplatesAdmin = lazy(() => import("@/pages/admin/rules/ChecklistTemplatesAdmin"));  // D38
const PerformanceTemplatesAdmin = lazy(() => import("@/pages/admin/rules/PerformanceTemplatesAdmin"));  // D39
const MISHub = lazy(() => import("@/pages/hr/MISHub"));  // D40
const MyChecklistTasks = lazy(() => import("@/pages/me/MyChecklistTasks"));  // D38
const PermissionMatrixV2 = lazy(() => import("@/pages/admin/rules/PermissionMatrixAdmin"));
// D54-b -- Designation permissions editor (feature-flag OFF surface).
const DesignationPermissionsAdmin = lazy(() => import("@/pages/admin/rbac/DesignationPermissionsAdmin"));
const UserPermissionOverrides = lazy(() => import("@/pages/admin/rules/UserPermissionOverridesAdmin"));  // D26
const UserOverridesAdmin = lazy(() => import("@/pages/admin/rules/UserOverridesAdmin"));  // D55-a
const AccessRequestsPage = lazy(() => import("@/pages/access-requests/AccessRequestsPage"));  // D55-d
const VaultHub          = lazy(() => import("@/pages/vault/VaultHub"));
const OfficeAssetsTab   = lazy(() => import("@/pages/front-desk/OfficeAssetsTab"));
const MyAssets          = lazy(() => import("@/pages/hr/MyAssets"));
// D53-hygiene -- HRControlPanel retired (shadow HR shell). Route
// /hr/control-panel now redirects to /hr; see the block below.
const SalaryMaster      = lazy(() => import("@/pages/hr/SalaryMaster"));     // D48
const FieldTripDetail   = lazy(() => import("@/pages/hr/attendance-hub/FieldTripDetail"));  // D51
const AssetDetail       = lazy(() => import("@/pages/hr/AssetDetail"));  // D52x
const DataModel         = lazy(() => import("@/pages/admin/DataModel"));                  // D52

// D51 -- wrap the lazy component so it can grab the :tripId route param.
function FieldTripDetailRoute() {
  const { tripId } = useParams();
  return <FieldTripDetail tripId={tripId} />;
}
const RulesLanding      = named(() => import("@/pages/admin/rules/RulesAdmin"), "RulesLanding");
// D48-c: legacy PermissionMatrix (nested-scope grid) retired -- replaced by
// PermissionMatrixV2 (D46 flat-key screen). File retained one soak cycle.
const Picklists         = named(() => import("@/pages/admin/rules/RulesAdmin"), "Picklists");
const Formulas          = named(() => import("@/pages/admin/rules/RulesAdmin"), "Formulas");
const FormulaEditor     = named(() => import("@/pages/admin/rules/RulesAdmin"), "FormulaEditor");
const Approvals         = named(() => import("@/pages/admin/rules/RulesAdmin"), "Approvals");
const Translations      = named(() => import("@/pages/admin/rules/RulesAdmin"), "Translations");
const Numbering         = named(() => import("@/pages/admin/rules/RulesAdmin"), "Numbering");
const CustomFields      = named(() => import("@/pages/admin/rules/RulesAdminPart2"), "CustomFields");
const Workflows         = named(() => import("@/pages/admin/rules/RulesAdminPart2"), "Workflows");
const WorkflowDesigner  = named(() => import("@/pages/admin/rules/RulesAdminPart2"), "WorkflowDesigner");
const Branding          = named(() => import("@/pages/admin/rules/RulesAdminPart2"), "Branding");
// D48-c: `NotifTemplates` embed from RulesAdminPart2 fully retired.
// Both `/admin/notifications-branding/templates` and
// `/admin/notifications/templates` render the dedicated
// `<NotificationsTemplates>` component. See patterns.md.
const OrgStructure      = named(() => import("@/pages/admin/rules/RulesAdminPart2"), "OrgStructure");
const SalesRulesLanding    = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesRulesLanding");
const SalesPipelineRules   = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesPipelineRules");
const SalesSourcesRules    = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesSourcesRules");
const SalesAssignmentRules = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesAssignmentRules");
const SalesDiscountRules   = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesDiscountRules");
const SalesGstRules        = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesGstRules");
const SalesQuoteRules      = named(() => import("@/pages/admin/rules/SalesRulesAdmin"), "SalesQuoteRules");
// -- HR hub --------------------------------------------------------------
const HrLayout          = lazy(() => import("@/pages/hr/HrLayout"));
const HrDashboard       = lazy(() => import("@/pages/hr/HrDashboard"));
const HrComingSoon      = lazy(() => import("@/pages/hr/HrComingSoon"));
const Employees         = lazy(() => import("@/pages/hr/Employees"));
const EmployeeProfile   = named(() => import("@/pages/hr/Employees"), "EmployeeProfile");
const Departments       = lazy(() => import("@/pages/hr/Departments"));
// D36.b -- legacy attendance pages retired to `_deprecated/`. Hub is canonical.
const AttendanceHub     = lazy(() => import("@/pages/hr/attendance-hub/AttendanceHub"));
const AttendanceFullEditor = lazy(() => import("@/pages/hr/attendance-hub/AttendanceFullEditor"));  // D36.b
// D37.a -- Tax declarations pages
const TaxDeclarations       = lazy(() => import("@/pages/me/TaxDeclarations"));
const TaxDeclarationsQueue  = lazy(() => import("@/pages/hr/TaxDeclarationsQueue"));
const Leave             = lazy(() => import("@/pages/hr/Leave"));
const Holidays          = lazy(() => import("@/pages/hr/Holidays"));  // D16
const WFHModule         = lazy(() => import("@/pages/wfh/WFHModule"));  // D20
const Expense           = lazy(() => import("@/pages/hr/Expense"));
const OrgChart          = lazy(() => import("@/pages/hr/OrgChart"));
const HrReports         = lazy(() => import("@/pages/hr/HrReports"));
const OnboardingPipeline = named(() => import("@/pages/hr/Onboarding"), "OnboardingPipeline");
const MyOnboarding       = named(() => import("@/pages/hr/Onboarding"), "MyOnboarding");
// -- Sales hub -----------------------------------------------------------
const SalesLayout       = lazy(() => import("@/pages/sales/SalesLayout"));
const SalesDashboardLanding = lazy(() => import("@/pages/sales/SalesDashboardLanding"));
const SalesAccounts     = named(() => import("@/pages/sales/SalesPages"), "Accounts");
const SalesAccountDetail = named(() => import("@/pages/sales/SalesPages"), "AccountDetail");
const SalesPipeline     = named(() => import("@/pages/sales/SalesPages"), "Pipeline");
const SalesQuotes       = named(() => import("@/pages/sales/Quotes"), "Quotes");
const SalesQuoteDetail  = named(() => import("@/pages/sales/Quotes"), "QuoteDetail");
const SalesInvoices     = named(() => import("@/pages/sales/Invoices"), "Invoices");
const SalesInvoiceDetail = named(() => import("@/pages/sales/Invoices"), "InvoiceDetail");
const SalesTargets      = named(() => import("@/pages/sales/SalesDashboard"), "SalesTargets");
const Visits            = named(() => import("@/pages/sales/VisitsAndReports"), "Visits");
const NewVisit          = named(() => import("@/pages/sales/VisitsAndReports"), "NewVisit");
const SalesReports      = named(() => import("@/pages/sales/VisitsAndReports"), "SalesReports");
const Configurator      = lazy(() => import("@/pages/sales/Configurator"));
const Designs           = lazy(() => import("@/pages/sales/Designs"));
const DesignerCompare   = lazy(() => import("@/pages/sales/DesignerCompare"));
// -- Service hub ---------------------------------------------------------
const ServiceLayout      = lazy(() => import("@/pages/service/ServiceLayout"));
const ServiceDashboard   = lazy(() => import("@/pages/service/ServiceDashboard"));
const TicketsList        = lazy(() => import("@/pages/service/TicketsList"));
const TicketDetail       = lazy(() => import("@/pages/service/TicketDetail"));
const Warranties         = lazy(() => import("@/pages/service/Warranties"));
const AMCDashboard       = lazy(() => import("@/pages/service/AMCDashboard"));
const InstallPipeline    = lazy(() => import("@/pages/service/InstallPipeline"));
const ComponentsRegistry = lazy(() => import("@/pages/service/ComponentsRegistry"));
// -- Front Desk hub ------------------------------------------------------
const FrontDeskLayout    = lazy(() => import("@/pages/front-desk/FrontDeskLayout"));
const FrontDeskDashboard = lazy(() => import("@/pages/front-desk/FrontDeskDashboard"));
const VisitorsHub        = lazy(() => import("@/pages/visitors/VisitorsHub"));
const CouriersHub        = lazy(() => import("@/pages/couriers/CouriersHub"));
const IdCardsHub         = lazy(() => import("@/pages/id_cards/IdCardsHub"));
const MeetingRoomsHub    = lazy(() => import("@/pages/meeting_rooms/MeetingRoomsHub"));
const OfficeSuppliesHub  = lazy(() => import("@/pages/office_supplies/OfficeSuppliesHub"));
const MeSupplies         = lazy(() => import("@/pages/me/MeSupplies"));
const MyReceptionHub     = lazy(() => import("@/pages/me/MyReceptionHub"));
const ManagerApprovals   = lazy(() => import("@/pages/manager/ManagerApprovals"));
// -- Chat / Meetings / Notes / PM ---------------------------------------
const ChatPage           = lazy(() => import("@/pages/chat/ChatPage"));
const ChatGodView        = lazy(() => import("@/pages/chat/GodView"));
const GodViewPage        = lazy(() => import("@/pages/godview/GodViewPage"));
const MeetingsList       = lazy(() => import("@/pages/meetings/MeetingsList"));
const MeetingDetail      = lazy(() => import("@/pages/meetings/MeetingDetail"));
const CalendarPage       = lazy(() => import("@/pages/calendar/CalendarPage"));
const MeetingSeriesDetail = lazy(() => import("@/pages/meetings/MeetingSeriesDetail"));
const NotesList          = lazy(() => import("@/pages/notes/NotesList"));
const NoteEditor         = lazy(() => import("@/pages/notes/NoteEditor"));
const PublicNote         = lazy(() => import("@/pages/notes/PublicNote"));
const TasksPage          = lazy(() => import("@/pages/tasks/TasksPage"));
const InboxPage          = lazy(() => import("@/pages/inbox/InboxPage"));
const ProjectsList       = lazy(() => import("@/pages/pm/ProjectsList"));
const ProjectDetail      = lazy(() => import("@/pages/pm/ProjectDetail"));
const DocEditor          = lazy(() => import("@/pages/pm/DocEditor"));
const Timesheet          = lazy(() => import("@/pages/pm/Timesheet"));
const CrossProjectCalendar = lazy(() => import("@/pages/pm/CrossProjectCalendar"));
// -- Me / Profile / Notifications ---------------------------------------
const MeDashboard        = lazy(() => import("@/pages/me/MeDashboard"));
const MeAttendance       = lazy(() => import("@/pages/me/MeAttendance"));
const MeLeaves           = lazy(() => import("@/pages/me/MeLeaves"));
const MeDocuments        = lazy(() => import("@/pages/me/MeDocuments"));
const MeNotifications    = lazy(() => import("@/pages/me/Notifications"));
const Profile            = lazy(() => import("@/pages/Profile"));
// D24 -- new pages: Announcements, Grievances, Referrals, Handbook.
const AnnouncementsPage  = lazy(() => import("@/pages/AnnouncementsPage"));
const GrievancesPage     = lazy(() => import("@/pages/GrievancesPage"));
const ReferralsPage      = lazy(() => import("@/pages/ReferralsPage"));
const HandbookPage       = lazy(() => import("@/pages/HandbookPage"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
// -- Orders / Dealers / Inventory / Reports -----------------------------
const OrdersList         = named(() => import("@/pages/OrdersPages"), "OrdersList");
const OrderDetail        = named(() => import("@/pages/OrdersPages"), "OrderDetail");
const InventoryStock     = named(() => import("@/pages/InventoryPages"), "InventoryStock");
const InventoryMovements = named(() => import("@/pages/InventoryPages"), "InventoryMovements");
const InventoryAlerts    = named(() => import("@/pages/InventoryPages"), "InventoryAlerts");
const OrdersReportTiles  = named(() => import("@/pages/ReportsOrdersTiles"), "OrdersReportTiles");
const DealersList        = named(() => import("@/pages/dealers/DealersPages"), "DealersList");
const DealerDetail       = named(() => import("@/pages/dealers/DealersPages"), "DealerDetail");
const DealersNetwork     = named(() => import("@/pages/dealers/DealersPages"), "DealersNetwork");
const DealerSchemes      = named(() => import("@/pages/dealers/DealersPages"), "DealerSchemes");
const DealerPayouts      = named(() => import("@/pages/dealers/DealersPages"), "DealerPayouts");
const DealerSchemeDetail = named(() => import("@/pages/dealers/SchemesAndPayouts"), "DealerSchemeDetail");
// -- Customer portal (separate flow) -------------------------------------
const CustomerShell        = lazy(() => import("@/pages/portal/CustomerShell"));
const CustomerHome         = lazy(() => import("@/pages/portal/CustomerHome"));
const CustomerTickets      = lazy(() => import("@/pages/portal/CustomerTickets"));
const CustomerTicketDetail = lazy(() => import("@/pages/portal/CustomerTicketDetail"));
const CustomerNewTicket    = lazy(() => import("@/pages/portal/CustomerNewTicket"));
const CustomerWarranties   = lazy(() => import("@/pages/portal/CustomerWarranties"));
const CustomerAMC          = lazy(() => import("@/pages/portal/CustomerAMC"));

const qc = new QueryClient();

function AdminGate({ children }) {
  const loading = useAuth((s) => s.loading);
  const canU = usePermission("users", "view");
  const canA = usePermission("audit", "view");
  // D31 -- HR_MANAGER / MANAGER need to reach the D26 Rules Engine page
  // so they can *file* grant requests (or view effective permissions for
  // their reports). AdminGate is the outer wrapper; the inner page still
  // renders read-only for anyone without user_permission_overrides_edit.
  const canPerms = useMatrixAny(
    "user_permission_overrides_view", "grant_request_create",
  );
  if (loading) return null;
  if (!canU && !canA && !canPerms) return <Navigate to="/" replace />;
  return children;
}

function CustomerRedirect({ children }) {
  const me = useAuth(s => s.user);
  if (me?.role === "CUSTOMER") return <Navigate to="/portal" replace />;
  return children;
}

function PortalGuard({ children }) {
  const me = useAuth(s => s.user);
  if (me && me.role !== "CUSTOMER") return <Navigate to="/" replace />;
  return children;
}

function ChatGodViewGate({ children }) {
  const me = useAuth(s => s.user);
  if ((me?.role || "").toUpperCase() !== "CEO") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ChatComplianceMount() {
  const me = useAuth(s => s.user);
  return <ChatCompliancePolicy user={me} />;
}

/* Dispatch 3.5 -- Force-change router guard.
 * If the current user carries ``force_password_change_on_next_login``,
 * every route inside AppShell short-circuits to /force-change-password.
 * The gate is explicit + separate from RequireAuth so /login,
 * /invite/:token, /reset-password and /public/notes/:token remain
 * accessible even when the flag is on. */
function ForceChangeGate({ children }) {
  const me = useAuth((s) => s.user);
  if (me?.force_password_change_on_next_login) {
    return <Navigate to="/force-change-password" replace />;
  }
  return children;
}

// Phase 25 (E.1) -- shared Suspense fallback. Uses the Home shimmer so the
// visual transition into any lazy route feels consistent.
function RouteFallback() {
  return <div className="p-4"><ShimmerDashboard /></div>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPasswordConsume />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/force-change-password" element={<RequireAuth><ForceChangePassword /></RequireAuth>} />
        {/* Phase 18 -- public read-only shared notes (no auth) */}
        <Route path="/public/notes/:token" element={<PublicNote />} />

        {/* Phase 6C-d -- Customer self-service portal */}
        <Route element={<RequireAuth><PortalGuard><CustomerShell /></PortalGuard></RequireAuth>}>
          <Route path="/portal" element={<CustomerHome />} />
          <Route path="/portal/tickets" element={<CustomerTickets />} />
          <Route path="/portal/tickets/new" element={<CustomerNewTicket />} />
          <Route path="/portal/tickets/:id" element={<CustomerTicketDetail />} />
          <Route path="/portal/warranties" element={<CustomerWarranties />} />
          <Route path="/portal/amc" element={<CustomerAMC />} />
        </Route>

        <Route element={<RequireAuth><CustomerRedirect><ForceChangeGate><DelightProvider><PushPermissionProvider><ChatComplianceMount /><AppShell /></PushPermissionProvider></DelightProvider></ForceChangeGate></CustomerRedirect></RequireAuth>}>
          <Route index element={<Dashboard />} />
          {/* Phase 3 HR */}
          <Route element={<HrLayout />}>
            <Route path="/hr" element={<HrDashboard />} />
            <Route path="/my-assets" element={<MyAssets />} />
            <Route path="/hr/employees" element={<Employees />} />
            <Route path="/hr/employees/:id" element={<EmployeeProfile />} />
            <Route path="/hr/departments" element={<Departments />} />
            <Route path="/hr/org-chart" element={<OrgChart />} />
            <Route path="/hr/attendance" element={<AttendanceHub />} />
            {/* D36.b -- full-page editable attendance grid for bulk edits */}
            <Route path="/hr/attendance/edit" element={<AttendanceFullEditor />} />
            <Route path="/hr/attendance/edit/:employeeId" element={<AttendanceFullEditor />} />
            <Route path="/hr/field-trips/:tripId" element={<FieldTripDetailRoute />} />   {/* D51 */}
            <Route path="/hr/assets/:assetId" element={<AssetDetail />} />   {/* D52x */}
            {/* D52 -- /admin/data-model moved OUT of HrLayout in D52-viz
                so the Admin sidebar (not HR) frames the page. Original
                route registration lives below in the AdminLayout block. */}
            {/* D43 -- sub-tab param namespaced to `?sub=` so the hub can
                be embedded inside HRControlPanel without the outer
                `?tab=` colliding with the inner sub-tab key. */}
            <Route path="/hr/attendance/analytics" element={<Navigate to="/hr/attendance?sub=trends" replace />} />
            <Route path="/hr/attendance-analytics" element={<Navigate to="/hr/attendance?sub=trends" replace />} />
            <Route path="/hr/attendance-legacy" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/field-activity" element={<Navigate to="/hr/attendance?sub=field" replace />} />
            <Route path="/hr/leave" element={<Leave />} />
            <Route path="/hr/holidays" element={<Holidays />} />
            <Route path="/hr/wfh" element={<WFHModule />} />
            <Route path="/hr/wfh/request" element={<WFHModule defaultTab="request" />} />
            <Route path="/hr/wfh/approvals" element={<WFHModule defaultTab="approvals" />} />
            <Route path="/hr/wfh/calendar" element={<WFHModule defaultTab="calendar" />} />
            <Route path="/hr/expense" element={<Expense />} />
            <Route path="/hr/reports" element={<HrReports />} />
            <Route path="/hr/payroll" element={<HrComingSoon moduleLabel="Payroll" plannedPhase="Phase 24" />} />
            <Route path="/hr/salary-master" element={<SalaryMaster />} />
            <Route path="/hr/performance" element={<HrComingSoon moduleLabel="Performance" plannedPhase="Phase 24" />} />
            {/* D53-hygiene -- Handbook + Grievances moved INSIDE HrLayout
                per user directive ("should be inside HR"). Original D24
                comment about "not inheriting HR secondary nav" is reversed:
                the sub-nav is now the desired chrome. Same top-level URLs
                preserved for bookmarks. Announcements + Referrals stay
                top-level (they're org-wide, not HR-scoped). */}
            <Route path="/handbook" element={<HandbookPage />} />
            <Route path="/grievances" element={<GrievancesPage />} />
          </Route>
          <Route path="/profile" element={<Profile />} />
          {/* D53-hygiene -- HRControlPanel retired. Kept as a redirect to
              /hr so existing bookmarks + deep links keep working. The
              shadow shell (duplicate HR sub-nav embedded inside a tab
              rail) was the root cause of the "two HR sidebars" bug. */}
          <Route path="/hr/control-panel" element={<Navigate to="/hr" replace />} />
          <Route path="/hr/control-panel/:tab" element={<Navigate to="/hr" replace />} />
          {/* D24 -- Announcements + Referrals stay as top-level routes;
              they're org-wide, not HR-scoped. */}
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/referrals" element={<ReferralsPage />} />
          {/* Dispatch 11 -- Vault is a top-level module, NOT an HR sub-route.
              Previously nested inside <HrLayout> which leaked the HR
              secondary nav ("DASHBOARD / EMPLOYEES / ATTENDANCE / ...")
              into the Vault chrome. Same class of bug as the Office
              Assets routing fix in Dispatch 7. See patterns.md
              "Hub chrome mount pattern". */}
          <Route path="/vault" element={<VaultHub />} />
          <Route path="/vault/:id" element={<VaultHub />} />
          <Route path="/me/profile" element={<Profile />} />
          <Route path="/settings/notifications" element={<NotificationSettings />} />
          <Route path="/me/notifications" element={<MeNotifications />} />
          <Route path="/chat/god-view" element={<ChatGodViewGate><ChatGodView /></ChatGodViewGate>} />
          {/* Phase 25.5 -- unified God View (chats + notes + tasks + meetings) */}
          <Route path="/god-view" element={<ChatGodViewGate><GodViewPage /></ChatGodViewGate>} />
          <Route path="/god-view/flagged" element={<ChatGodViewGate><GodViewPage /></ChatGodViewGate>} />
          {/* Phase 4A -- Sales */}
          <Route element={<SalesLayout />}>
            <Route path="/sales" element={<SalesDashboardLanding />} />
            <Route path="/sales/accounts" element={<SalesAccounts />} />
            <Route path="/sales/accounts/:id" element={<SalesAccountDetail />} />
            <Route path="/sales/pipeline" element={<SalesPipeline />} />
            <Route path="/sales/quotes" element={<SalesQuotes />} />
            <Route path="/sales/quotes/:id" element={<SalesQuoteDetail />} />
            <Route path="/sales/invoices" element={<SalesInvoices />} />
            <Route path="/sales/invoices/:id" element={<SalesInvoiceDetail />} />
            <Route path="/sales/visits" element={<Visits />} />
            <Route path="/sales/visits/new" element={<NewVisit />} />
            <Route path="/sales/reports" element={<SalesReports />} />
            <Route path="/sales/targets" element={<SalesTargets />} />
            <Route path="/sales/leads" element={<HrComingSoon moduleLabel="Leads" plannedPhase="Phase 22+" />} />
            <Route path="/sales/contacts" element={<HrComingSoon moduleLabel="Contacts" plannedPhase="Phase 22+" />} />
            <Route path="/sales/products" element={<HrComingSoon moduleLabel="Products" plannedPhase="Phase 22+" />} />
          </Route>
          <Route path="/sales/configurator" element={<Configurator />} />
          <Route path="/sales/designer/designs" element={<Designs />} />
          <Route path="/sales/designer/compare" element={<DesignerCompare />} />
          <Route path="/dealers" element={<DealersList />} />
          <Route path="/dealers/network" element={<DealersNetwork />} />
          <Route path="/dealers/:id" element={<DealerDetail />} />
          <Route path="/dealer-schemes" element={<DealerSchemes />} />
          <Route path="/dealer-schemes/:id" element={<DealerSchemeDetail />} />
          <Route path="/dealer-payouts" element={<DealerPayouts />} />
          <Route path="/orders" element={<OrdersList />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/inventory" element={<InventoryStock />} />
          <Route path="/inventory/movements" element={<InventoryMovements />} />
          <Route path="/inventory/alerts" element={<InventoryAlerts />} />
          <Route path="/reports" element={<OrdersReportTiles />} />
          <Route path="/front-desk" element={<FrontDeskLayout />}>
            <Route index element={<FrontDeskDashboard />} />
            <Route path="visitors" element={<VisitorsHub />} />
            <Route path="visitors/*" element={<VisitorsHub />} />
            <Route path="couriers" element={<CouriersHub />} />
            <Route path="couriers/*" element={<CouriersHub />} />
            <Route path="id-cards" element={<IdCardsHub />} />
            <Route path="id-cards/*" element={<IdCardsHub />} />
            <Route path="meeting-rooms" element={<MeetingRoomsHub />} />
            <Route path="meeting-rooms/*" element={<MeetingRoomsHub />} />
            <Route path="office-supplies" element={<OfficeSuppliesHub />} />
            <Route path="office-supplies/*" element={<OfficeSuppliesHub />} />
            <Route path="office-assets" element={<OfficeAssetsTab />} />
          </Route>
          {/* Legacy per-module URL redirects */}
          <Route path="/office-supplies"      element={<HubRedirect toBase="/front-desk/office-supplies" message="Office Supplies moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/office-supplies/*"    element={<HubRedirect toBase="/front-desk/office-supplies" message="Office Supplies moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/visitors"             element={<HubRedirect toBase="/front-desk/visitors" message="Visitors moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/visitors/*"           element={<HubRedirect toBase="/front-desk/visitors" message="Visitors moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/couriers"             element={<HubRedirect toBase="/front-desk/couriers" message="Couriers moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/couriers/*"           element={<HubRedirect toBase="/front-desk/couriers" message="Couriers moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/id-cards"             element={<HubRedirect toBase="/front-desk/id-cards" message="ID Cards moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/id-cards/*"           element={<HubRedirect toBase="/front-desk/id-cards" message="ID Cards moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/meeting-rooms"        element={<HubRedirect toBase="/front-desk/meeting-rooms" message="Meeting Rooms moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/meeting-rooms/*"      element={<HubRedirect toBase="/front-desk/meeting-rooms" message="Meeting Rooms moved to the Front Desk hub. Bookmark updated." />} />
          <Route path="/me/supplies" element={<MeSupplies />} />
          <Route path="/me/reception" element={<MyReceptionHub />} />
          <Route path="/manager/approvals" element={<ManagerApprovals />} />
          <Route element={<ServiceLayout />}>
            <Route path="/service" element={<ServiceDashboard />} />
            <Route path="/service/tickets" element={<TicketsList />} />
            <Route path="/service/tickets/:id" element={<TicketDetail />} />
            <Route path="/service/warranties" element={<Warranties />} />
            <Route path="/service/warranties/components" element={<ComponentsRegistry />} />
            <Route path="/service/amc" element={<AMCDashboard />} />
            <Route path="/service/installations" element={<InstallPipeline />} />
            <Route path="/service/schedules" element={<HrComingSoon moduleLabel="Service Schedules" plannedPhase="Phase 22+" />} />
          </Route>
          <Route path="/tenders" element={<ComingSoon titleKey="nav.tenders" phase={5} />} />
          <Route path="/competition" element={<ComingSoon titleKey="nav.competition" phase={5} />} />
          <Route path="/suppliers" element={<ComingSoon titleKey="nav.suppliers" phase={5} />} />
          <Route element={<AdminGate><AdminLayout /></AdminGate>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/all-tools" element={<AdminHub />} />
            {/* Phase 20.5b -- 12 canonical admin URLs. Old paths get client-side
                redirects below via <HubRedirect>. Every internal Link now
                points at the canonical NEW path so no double-hops. */}
            <Route path="/admin/access/users" element={<AdminUsers />} />
            <Route path="/admin/access/audit-log" element={<AuditLog />} />
            {/* D48-c: /admin/access/permissions-matrix (plural, legacy)
                now renders the D46 PermissionMatrixAdmin. Sidebar +
                bookmarks keep working; legacy screen retired to
                /app/legacy_retired/. */}
            {/* D54-e Issue A -- Legacy role-columns matrix retired as the
                primary permissions entry. The canonical editor is now the
                Dept × Designation grid at /admin/rbac/designations. Old
                URL redirects there. The legacy view stays reachable at
                /admin/rbac/legacy-role-matrix for one soak cycle. */}
            <Route path="/admin/access/permissions-matrix" element={<HubRedirect toBase="/admin/rbac/designations" message="Permissions moved to the D54 Designation Matrix." />} />
            <Route path="/admin/rbac/legacy-role-matrix" element={<PermissionMatrixV2 />} />
            <Route path="/admin/access/module-visibility" element={<ModuleVisibilityRules />} />
            {/* D52 -- Data Model page (2D SVG in D52; 3D graph in D52-viz).
                Moved here from the HrLayout block so the Admin sidebar
                frames it correctly. Route path is unchanged. */}
            <Route path="/admin/data-model" element={<DataModel />} />
            {/* D48-c: legacy /admin/notifications-branding/templates URL
                renders the survivor <NotificationsTemplates>. Former
                embed from RulesAdminPart2 fully deleted. */}
            <Route path="/admin/notifications-branding/templates" element={<NotificationsTemplates />} />
            <Route path="/admin/notifications-branding/branding" element={<Branding />} />
            <Route path="/admin/notifications-branding/smtp" element={<SmtpAdmin />} />
            <Route path="/admin/data-ops/cleanup" element={<DataCleanupAdmin />} />
            <Route path="/admin/data-ops/ai-cost" element={<AiCostAdmin />} />
            <Route path="/admin/organization/branches-departments" element={<OrgStructure />} />
            <Route path="/admin/organization/approvals" element={<Approvals />} />
            <Route path="/admin/organization/formulas" element={<Formulas />} />
            <Route path="/admin/organization/formulas/:key" element={<FormulaEditor />} />
            {/* Legacy 12 -- one-shot toast + client-side redirect. Kept under
                AdminGate so unauthorised visitors still get the gate first. */}
            <Route path="/admin/users"                    element={<HubRedirect toBase="/admin/access/users" message="Users moved to Access & Security. Bookmark updated." />} />
            <Route path="/admin/audit"                    element={<HubRedirect toBase="/admin/access/audit-log" message="Audit Log moved to Access & Security. Bookmark updated." />} />
            <Route path="/admin/rules/permissions"        element={<HubRedirect toBase="/admin/access/permissions-matrix" message="Permissions Matrix moved to Access & Security. Bookmark updated." />} />
            <Route path="/admin/rules/module-visibility"  element={<HubRedirect toBase="/admin/access/module-visibility" message="Module Visibility moved to Access & Security. Bookmark updated." />} />
            <Route path="/admin/rules/notifications"      element={<HubRedirect toBase="/admin/notifications-branding/templates" message="Notification Templates moved to Notifications & Branding. Bookmark updated." />} />
            <Route path="/admin/rules/branding"           element={<HubRedirect toBase="/admin/notifications-branding/branding" message="Branding moved to Notifications & Branding. Bookmark updated." />} />
            <Route path="/admin/rules/smtp"               element={<HubRedirect toBase="/admin/notifications-branding/smtp" message="SMTP moved to Notifications & Branding. Bookmark updated." />} />
            <Route path="/admin/rules/data-cleanup"       element={<HubRedirect toBase="/admin/data-ops/cleanup" message="Data Cleanup moved to Data & Ops. Bookmark updated." />} />
            <Route path="/admin/rules/ai-cost"            element={<HubRedirect toBase="/admin/data-ops/ai-cost" message="AI Cost moved to Data & Ops. Bookmark updated." />} />
            <Route path="/admin/rules/org"                element={<HubRedirect toBase="/admin/organization/branches-departments" message="Branches & Departments moved to Organization. Bookmark updated." />} />
            <Route path="/admin/rules/approvals"          element={<HubRedirect toBase="/admin/organization/approvals" message="Approvals moved to Organization. Bookmark updated." />} />
            <Route path="/admin/rules/formulas"           element={<HubRedirect toBase="/admin/organization/formulas" message="Formulas moved to Organization. Bookmark updated." />} />
            <Route path="/admin/rules/formulas/:key"      element={<HubRedirect toBase="/admin/organization/formulas" message="Formulas moved to Organization. Bookmark updated." />} />
            {/* Unchanged Admin routes below. */}
            <Route path="/admin/production-reset" element={<ProductionReset />} />
            <Route path="/admin/llm-usage" element={<LlmUsage />} />
            <Route path="/admin/automations" element={<AutomationsAdmin />} />
            <Route path="/admin/demo-data" element={<DemoData />} />
            <Route path="/admin/chat-compliance" element={<ChatCompliance />} />
            <Route path="/admin/rules" element={<RulesLanding />} />
            <Route path="/admin/rules/hr" element={<HrRulesAdmin />} />
            <Route path="/admin/rules/pm-toggles" element={<PmRulesToggles />} />
            <Route path="/admin/rules/attendance-geofences" element={<GeofenceAdmin />} />
            <Route path="/admin/rules/organization" element={<OrgTaxonomyAdmin />} />
            <Route path="/admin/rules/security-policy" element={<SecurityPolicyAdmin />} />
            <Route path="/admin/rules/company-legal" element={<CompanyLegalAdmin />} />  {/* D37.b */}
            <Route path="/admin/rules/checklists" element={<ChecklistTemplatesAdmin />} />  {/* D38 */}
            <Route path="/admin/rules/performance" element={<PerformanceTemplatesAdmin />} />  {/* D39 */}
            <Route path="/admin/access/security-policy" element={<SecurityPolicyAdmin />} />
            <Route path="/admin/access/permission-matrix" element={<PermissionMatrixV2 />} />
            <Route path="/admin/rules/permission-matrix" element={<PermissionMatrixV2 />} />
            {/* D54-b -- Designation permissions editor. */}
            <Route path="/admin/rbac/designations" element={<DesignationPermissionsAdmin />} />
            <Route path="/admin/rules/user-permissions" element={<UserPermissionOverrides />} />  {/* D26 */}
            <Route path="/admin/rbac/user-overrides" element={<UserOverridesAdmin />} />  {/* D55-a */}
            <Route path="/admin/rules/ai" element={<AiRulesAdmin />} />
            <Route path="/admin/rules/ai-actions" element={<AiActionsLog />} />
            <Route path="/admin/rules/fields" element={<CustomFields />} />
            <Route path="/admin/rules/picklists" element={<Picklists />} />
            <Route path="/admin/rules/workflows" element={<Workflows />} />
            <Route path="/admin/rules/workflows/:key" element={<WorkflowDesigner />} />
            <Route path="/admin/rules/translations" element={<Translations />} />
            <Route path="/admin/rules/numbering" element={<Numbering />} />
            <Route path="/admin/rules/sales" element={<SalesRulesLanding />} />
            <Route path="/admin/rules/sales/pipeline" element={<SalesPipelineRules />} />
            <Route path="/admin/rules/sales/sources" element={<SalesSourcesRules />} />
            <Route path="/admin/rules/sales/assignment" element={<SalesAssignmentRules />} />
            <Route path="/admin/rules/sales/discount" element={<SalesDiscountRules />} />
            <Route path="/admin/rules/sales/gst" element={<SalesGstRules />} />
            <Route path="/admin/rules/sales/quote" element={<SalesQuoteRules />} />
            <Route path="/admin/rules/sales/targets" element={<SalesTargets />} />
            <Route path="/admin/notifications/templates" element={<NotificationsTemplates />} />
          </Route>
          <Route path="/projects" element={<ProjectsList />} />
          <Route path="/projects/calendar" element={<CrossProjectCalendar />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/projects/:id/docs/:docId" element={<DocEditor />} />
          <Route path="/me/timesheet" element={<Timesheet />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/hr/onboarding" element={<OnboardingPipeline />} />
          <Route path="/me" element={<MeDashboard />} />
          <Route path="/me/attendance" element={<MeAttendance />} />
          <Route path="/me/tax-declarations" element={<TaxDeclarations />} />
          <Route path="/me/checklists" element={<MyChecklistTasks />} />  {/* D38 - moved out of AdminGate */}
          <Route path="/hr/mis" element={<MISHub />} />  {/* D40 - MIS + Cockpit + Overview */}
          <Route path="/hr/tax-declarations/queue" element={<TaxDeclarationsQueue />} />
          <Route path="/me/leaves" element={<MeLeaves />} />
          <Route path="/me/documents" element={<MeDocuments />} />
          <Route path="/me/onboarding" element={<MyOnboarding />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/notes" element={<NotesList />} />
          <Route path="/notes/:id" element={<NoteEditor />} />
          <Route path="/meetings" element={<MeetingsList />} />
          <Route path="/meetings/series/:id" element={<MeetingSeriesDetail />} />
          <Route path="/meetings/:id" element={<MeetingDetail />} />
          {/* Phase 21 -- Unified Calendar. */}
          <Route path="/calendar" element={<CalendarPage />} />

          {/* D55-d / D55d-uat3 -- Access Requests. Universal route (available
              to every authenticated user regardless of role). Previously
              nested inside <AdminGate> which was blocking non-admin users
              from reaching the page. Moved out to fix discoverability. */}
          <Route path="/access-requests" element={<AccessRequestsPage />} />
          <Route path="/access-requests/mine" element={<AccessRequestsPage />} />
          <Route path="/access-requests/inbox" element={<AccessRequestsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

const RequestAccessBridge = lazy(() => import("@/pages/access-requests/AccessRequestsPage").then((m) => ({ default: m.RequestModal })));
// D55-d Part 2 -- global bridge that listens for `rbac:access-denied`
// events (fired by api.js interceptors on any 403 with a
// `denied_permission_key` payload) and opens the Request Modal
// prefilled with the denied key.
//
// D55h hardening -- systemic "page needs refresh to render" report
// traced back (potentially) to this bridge intercepting a 403 fired
// during initial auth hydration on a fresh route. Two guardrails:
//   1. `loading === true` -> ignore the event. RequireAuth blocks
//      render anyway; the modal opening on top of a still-hydrating
//      page steals focus and looks like a blank page.
//   2. Mount debounce -- for 400ms after this component mounts, drop
//      events without setting state. This absorbs the click-then-mount
//      race where a lazy chunk arrives and starts firing API calls
//      before the router has fully settled.
// After the guard window closes, 403s open the modal exactly like before.
function RbacDeniedBridge() {
  const [state, setState] = React.useState({ open: false, key: "" });
  const loading = useAuth((s) => s.loading);
  const loadingRef = React.useRef(loading);
  React.useEffect(() => { loadingRef.current = loading; }, [loading]);
  React.useEffect(() => {
    const mountedAt = Date.now();
    const onDenied = (e) => {
      // Drop during initial auth hydration.
      if (loadingRef.current) return;
      // Drop during 400ms mount-race window.
      if (Date.now() - mountedAt < 400) return;
      const k = e?.detail?.permission_key || "";
      if (k) setState({ open: true, key: k });
    };
    window.addEventListener("rbac:access-denied", onDenied);
    return () => window.removeEventListener("rbac:access-denied", onDenied);
  }, []);
  if (!state.open) return null;
  return (
    <Suspense fallback={null}>
      <RequestAccessBridge
        open={state.open}
        prefillKey={state.key}
        onClose={() => setState({ open: false, key: "" })}
        onCreated={() => setState({ open: false, key: "" })}
      />
    </Suspense>
  );
}

function AppInner() {
  const { hydrate } = useAuth();
  useEffect(() => { hydrate(); }, [hydrate]);
  return (
    <>
      <AppRoutes />
      <RbacDeniedBridge />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={qc}>
        <ThemeProvider>
          <BrowserRouter>
            <AppInner />
            <Sonner position="top-right" richColors closeButton />
            <BugReportModal />
            <ConfirmDialogRoot />
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
